import { useCallback, useMemo } from 'react';
import { HomePageData } from '../../domain/models/HomePageData';
import { adaptHomePageDataDtoToDomain } from '../../infra/mappers/home.mapper';
import { useGetHomePageDataQuery } from '../../infra/adapters/home.adapter';

interface HomeServiceInterface {
  getHomePageData(): Promise<HomePageData>;
}

export interface UseHomeServiceOptions {
  service: HomeServiceInterface;
}

export interface UseHomeServiceReturn {
  homePageData: HomePageData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Reads home page data from RTK Query cache synchronously so SSR and first client render
 * have data (cache is seeded by ReduxProvider from SSR/window). Refetch uses the query hook.
 */
export function useHomeService({ service: _service }: UseHomeServiceOptions): UseHomeServiceReturn {
  const { data: cachedData, isLoading: isQueryLoading, error: queryError, refetch: refetchQuery } = useGetHomePageDataQuery(
    {},
    { refetchOnMountOrArgChange: false }
  );

  const homePageData = useMemo(() => {
    if (cachedData && typeof cachedData === 'object' && cachedData !== null) {
      try {
        return adaptHomePageDataDtoToDomain(cachedData as Record<string, unknown>);
      } catch {
        return null;
      }
    }
    return null;
  }, [cachedData]);

  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  return {
    homePageData,
    isLoading: isQueryLoading,
    error: queryError ? (queryError as { message?: string })?.message ?? 'Failed to fetch home page data' : null,
    refetch,
  };
}
